import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ITransactionStatus,
  ITransactionType,
} from '~/@api/generated-api-types-account/Api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/shadcn/ui/table'
import { Spinner } from '~/shadcn/ui/spinner'
import { CurrencyFormatter } from '~/@mid_ui/currency-formatter/CurrencyFormatter'
import { DateTimeFormatter } from '~/@mid_ui/datetime-formatter/DateTimeFormatter'
import { PageContainer } from '~/@mid_ui/page-container/PageContainer'
import { FiltersContainer } from '~/@mid_ui/table/FiltersContainer'
import { HeadingTwo } from '~/@mid_ui/typography/HeadingTwo'
import { ProjectLabel } from '~/@pages_investing/projects/_projects_ui/project-popup-components/ProjectLabel'
import { TabTitleWrapper } from '~/@pages_account-portfolio/financial-overview/TabTitleWrapper'
import { getTransactionStatusLabel } from '../transactions_ui/transaction-name/labelByTransactionStatus'
import { type Transaction } from '../_transaction_types/transaction.types'
import { TransactionTypeLabel } from '../transactions_ui/transaction-type/TransactionType'
import { useTransactionDownloads } from '../transactions-pdf/useTransactionPdf'
import { useTransactionsQuery } from './useTransactionsQuery'
import { TransactionTypeFilter } from '../_transaction_filtering/transaction-type-filter/TransactionTypeFilter'
import { TransactionDateRangeFilter } from '../_transaction_filtering/transaction-daterange-filter/TransactionDateRangeFilter'
import { NoTransactions } from '../transactions_ui/no-transactions/NoTransactions'
import { useTransactionsFilters } from './useTransactionsFilters'
import { Button } from '~/shadcn/ui/button/button'

type QueryParams = {
  project?: {
    businessId?: number
    stageNumber?: number
  }
}

export function TransactionsTable() {
  const { project }: QueryParams = useParams()
  const navigate = useNavigate()
  const {
    transactionTypeId,
    setTransactionTypeId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = useTransactionsFilters()
  const {
    data,
    isFetching: isLoading,
    fetchNextPage,
    hasNextPage,
  } = useTransactionsQuery({
    project,
    transactionId: transactionTypeId,
    startDate,
    endDate,
  })
  const { downloadRevenueFeeTaxDocumentPdf } = useTransactionDownloads()

  const isFiltersApplied = _isFiltersApplied(
    transactionTypeId,
    startDate,
    endDate
  )

  if (isLoading && !data) {
    return (
      <div className="flex justify-center items-center mt-7 md:mt-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  const transactions = data?.pages.flatMap(({ items }) => items) ?? []

  return (
    <>
      <div className="hidden md:flex border-b border-border">
        <TabTitleWrapper>
          <HeadingTwo>Transakcie</HeadingTwo>
        </TabTitleWrapper>
      </div>

      <div className="flex flex-col">
        <FiltersContainer colorScheme="light" className="border-b-0 py-10">
          <PageContainer className="flex flex-col md:flex-row items-end justify-start gap-5">
            <TransactionTypeFilter
              value={transactionTypeId}
              onValueChange={setTransactionTypeId}
            />

            <TransactionDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onValueChange={(startDate, endDate) => {
                setStartDate(startDate)
                setEndDate(endDate)
              }}
            />
          </PageContainer>
        </FiltersContainer>

        <PageContainer className="pb-15 md:pb-30">
          {transactions.length === 0 && (
            <NoTransactions isIconDisplayed={!isFiltersApplied} />
          )}
          {transactions.length > 0 && (
            <Table aria-label="customized table">
              <TableHeader className="h-11">
                <TableRow>
                  <TableHead className="border-border">Dátum a čas</TableHead>
                  <TableHead className="border-border">
                    Moja investícia
                  </TableHead>
                  <TableHead className="border-border">Účet</TableHead>
                  <TableHead className="border-border">Status</TableHead>
                  <TableHead className="border-border text-right">
                    Suma
                  </TableHead>
                  <TableHead className="border-border">Typ platby</TableHead>
                  <TableHead className="border-border">Akcie</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody className="">
                {transactions.map((row) => {
                  const { investment } = row

                  return (
                    <TableRow key={row._id} className="border-border">
                      <TableCell>
                        <DateTimeFormatter value={row.createdAt} />
                      </TableCell>
                      <TableCell>
                        {investment?.sign && (
                          <div className="flex flex-col gap-1">
                            <Link
                              to={`/account/investments?investmentId=${investment._id}`}
                              className="font-semibold text-gray-500 text-sm leading-6"
                              onClick={(event) => {
                                event.preventDefault()
                                navigate(
                                  `/account/investments?investmentId=${investment._id}`
                                )
                              }}>
                              ID: {investment.sign}
                            </Link>
                            {row.project?.collateralInfo?.collateralCategory &&
                              row.project.collateralInfo
                                .collateralSubcategory && (
                                <ProjectLabel
                                  category={
                                    row.project.collateralInfo
                                      .collateralCategory
                                  }
                                  subcategory={
                                    row.project.collateralInfo
                                      .collateralSubcategory
                                  }
                                />
                              )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-base leading-6">
                        Investičný
                      </TableCell>
                      <TableCell className="text-base leading-6">
                        {getTransactionStatusLabel(row.statusId)}
                      </TableCell>
                      <TableCell className="text-right text-base md:text-lg">
                        <CurrencyFormatter
                          value={row.value}
                          className="font-semibold"
                          integerClassName="text-xl"
                          decimalClassName="text-sm"
                        />
                      </TableCell>
                      <TableCell className="text-sm leading-6">
                        <TransactionTypeLabel
                          typeId={row.typeId}
                          flag={row.flag}
                        />
                      </TableCell>
                      <TableCell className="text-sm leading-6">
                        {_canDownloadInvoice(row) && (
                          <Link
                            to="#"
                            className="text-decoration-underline hover:text-black"
                            onClick={(e) => {
                              e.preventDefault()
                              downloadRevenueFeeTaxDocumentPdf(row._id)
                            }}>
                            Faktúra
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
          <div className="flex justify-center items-center pt-7 md:pt-10">
            {hasNextPage && (
              <Button
                variant="outline"
                disabled={isLoading}
                onClick={() => fetchNextPage()}>
                Zobraziť ďalšie transakcie
              </Button>
            )}
          </div>
        </PageContainer>
      </div>
    </>
  )
}

function _canDownloadInvoice(transaction: Transaction) {
  return (
    transaction.typeId === ITransactionType.InvestmentRevenueFee &&
    transaction.statusId === ITransactionStatus.Confirmed &&
    transaction.revenueFeeMonthlyInvoiceCode != null
  )
}

function _isFiltersApplied(
  transactionTypeId: string,
  startDate: Date,
  endDate: Date
) {
  const compStartDate = new Date('2000-01-01')
  compStartDate.setHours(0, 0, 0, 0)
  const compEndDate = new Date()
  compEndDate.setHours(23, 59, 59, 999)

  return (
    transactionTypeId !== 'all' ||
    startDate.getTime() !== compStartDate.getTime() ||
    endDate.getTime() !== compEndDate.getTime()
  )
}
